import { addRowError } from "@global-state/pages/admin/myProxies/slice";
import { IProxy } from "@interfaces/admin/proxy";
import { EOperator } from "@interfaces/operator.enum";
import { authApi } from "@RTKQuery/basic";

const proxyAuthApi = authApi.injectEndpoints({
    endpoints: (build) => ({
        getAllProxies: build.query<{
            parent_proxies: IProxy[],
            count_items: number
        }, {
            page: number,
            limit: number,
            router_id: string,
            phone_number: string,
            operator: string,
            selling: string
        }>({
            query: (params) => ({
                url: "proxies",
                params: Object.assign(
                    {
                        page: params.page,
                        size: params.limit
                    },
                    params.router_id ? {
                        router_id: Number(params.router_id)
                    } : {},
                    params.phone_number ? {
                        phone_number: params.phone_number
                    } : {},
                    params.operator ? {
                        operator: params.operator
                    } : {},
                    params.selling ? {
                        selling: params.selling === "true" ? true : false
                    } : {},
                )
            }),
            providesTags: ["ProxyAdmin"]
        }),
        loadUpProxies: build.mutation<void, void>({
            query: () => "proxies/update_from_api",
            invalidatesTags: ["ProxyAdmin"]
        }),
        deleteProxy: build.mutation<{ id: number, router_id: number }, number>({
            query: (proxyId) => ({ url: `proxies/${proxyId}`, method: "DELETE" }),
            invalidatesTags: (result, error) => error ? [] : ["ProxyAdmin"],
        }),
        updatePasswords: build.mutation<void, void>({
            query: () => "proxies/api_passwords",
        }),
        updateProxyData: build.mutation<void, {
            proxy_id: IProxy["id"],
            parent_id?: IProxy["id"],
            params: {
                page: number,
                limit: number,
                router_id: string,
                phone_number: string,
                operator: string,
                selling: string
            },
            body: Partial<Pick<IProxy, "operator" | "phone_number" | "auto_pay" | "pay_sum" | "pay_date" | "selling" | "day_quantity" | "tariff">>
        }>({
            query: ({ proxy_id, body }) => ({
                url: "proxies/" + proxy_id,
                method: "PUT",
                body: "tariff" in body ? {
                    tariff_id: body.tariff?.id
                } : body
            }),
            async onQueryStarted({ params, body, proxy_id, parent_id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    proxyAuthApi.util.updateQueryData('getAllProxies', params, (draft) => {
                        const targetId = parent_id ?? proxy_id;
                        const parent = draft.parent_proxies.find(item => item.id === targetId);

                        if (!parent) return;

                        if (parent_id) {
                            const child = parent.children.find(child => child.id === proxy_id);
                            if (child) Object.assign(child, body);
                        } else {
                            Object.assign(parent, body);
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(addRowError({
                        proxyId: proxy_id,
                        colName: Object.keys(body)[0]
                    }))
                    patchResult.undo()
                }
            },
        }),
        getProxyRouterIds: build.query<{
            id: number,
            router_id: IProxy["id"],
            parent_id: IProxy["id"],
            children_ids: IProxy["id"][]
        }[], void>({
            query: () => "proxies/ids",
            providesTags: ["ProxyAdmin"],
        }),
        connectAndDisconnectProxies: build.mutation<void, {
            parent_proxy_id: IProxy["id"],
            connect_router_ids: IProxy["id"][],
            disconnect_router_ids: IProxy["id"][],
        }>({
            query: ({ parent_proxy_id, ...body }) => ({
                url: `proxies/${parent_proxy_id}/manage`,
                method: "PUT",
                body
            })
        }),
        paymentStatusProxy: build.query<boolean, {
            id: number,
            token: string
        }>({
            query: (params) => ({
                url: "proxies/u_s",
                params
            })
        })
    }),
    overrideExisting: false,
})

export const {
    useGetAllProxiesQuery,
    useLoadUpProxiesMutation,
    useDeleteProxyMutation,
    useUpdatePasswordsMutation,
    useUpdateProxyDataMutation,
    useGetProxyRouterIdsQuery,
    useConnectAndDisconnectProxiesMutation,
    usePaymentStatusProxyQuery
} = proxyAuthApi;
